import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "@/app/providers";

/**
 * Gate for authenticated-only route subtrees. While auth is being checked,
 * renders a minimal fallback. Unauthenticated visitors are redirected to "/"
 * with the original location stored in `state.from` so the login flow can
 * round-trip them back after success.
 *
 * Use as a wrapper element in routes.tsx:
 *
 *   {
 *     path: "/workspaces",
 *     element: (
 *       <ProtectedRoute>
 *         <AppLayout>
 *           <Outlet />
 *         </AppLayout>
 *       </ProtectedRoute>
 *     ),
 *     children: [...],
 *   }
 *
 * See frontend/FRONTEND_AGENT_RULES.md §6 — protected routes redirect, they
 * don't render the modal-after-mount fallback.
 */

function AuthCheckFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center p-6">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { data, isPending } = useAuth();
  const location = useLocation();

  if (isPending) {
    return <AuthCheckFallback />;
  }

  if (!data) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
